import {
  ChangeDetectionStrategy, Component, EventEmitter,
  Input, OnChanges, Output, SimpleChanges, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AddedGuest, ContactItem, GuestWizardStep,
} from '../../../features/pcheck/models/pcheck.model';

@Component({
  selector: 'app-guest-wizard',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './guest-wizard.component.html',
  styleUrls: ['./guest-wizard.component.scss'],
})
export class GuestWizardComponent implements OnChanges {
  /**
   * SF contacts available to pick from.
   * Pass an empty array [] if none; pass undefined to hide the
   * "select from contacts" option entirely.
   */
  @Input() contacts?: ContactItem[];
  /**
   * 'booking'          — shows ask Yes/No first (standard booking flow).
   * 'add-participants' — starts directly at choose-method (no Yes/No prompt).
   */
  @Input() mode: 'booking' | 'add-participants' = 'booking';
  /** When true, all interactive buttons are disabled (e.g. this is a past message). */
  @Input() disabled = false;
  /**
   * Guests already added before this wizard opened (e.g. the user re-opened the
   * picker to add more). The wizard starts pre-filled with these so the user can
   * append or remove rather than losing the previous selection.
   */
  @Input() initialGuests?: AddedGuest[];

  /** Emitted with the final list of guests when the user confirms. */
  @Output() guestsConfirmed = new EventEmitter<AddedGuest[]>();
  /** Emitted when the user dismisses / cancels without selecting anyone. */
  @Output() cancelled = new EventEmitter<void>();

  readonly step         = signal<GuestWizardStep>('ask');
  readonly addedGuests  = signal<AddedGuest[]>([]);
  readonly contactSearch = signal('');
  readonly emailInput   = signal('');
  readonly emailError   = signal('');

  ngOnChanges(changes: SimpleChanges): void {
    // Only reset wizard state when `mode` changes (booking ↔ add-participants).
    // Contacts loading (undefined → array) must NOT wipe guests the user already added.
    // `mode` is set once on init, so this also runs the first-render seed below.
    if (changes['mode']) {
      this.step.set(this.mode === 'add-participants' ? 'choose-method' : 'ask');
      // Pre-fill with guests added before this wizard opened (re-opened picker),
      // so the user edits the running list instead of starting from scratch.
      this.addedGuests.set(this.initialGuests ? [...this.initialGuests] : []);
      this.contactSearch.set('');
      this.emailInput.set('');
      this.emailError.set('');
    }
  }

  // ── Computed ────────────────────────────────────────────────────────────────

  get hasContacts(): boolean {
    return Array.isArray(this.contacts) && this.contacts.length > 0;
  }

  get filteredContacts(): ContactItem[] {
    const q = this.contactSearch().toLowerCase().trim();
    if (!q) return this.contacts ?? [];
    return (this.contacts ?? []).filter(c =>
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.title?.toLowerCase().includes(q),
    );
  }

  // ── Step navigation ─────────────────────────────────────────────────────────

  onYes(): void  { this.step.set('choose-method'); }
  onNo(): void   { this.guestsConfirmed.emit(this.addedGuests()); this.step.set('done'); }
  onCancel(): void { this.cancelled.emit(); }

  onChooseContact(): void {
    this.contactSearch.set('');
    this.step.set('pick-contact');
  }

  onChooseEmail(): void {
    this.emailInput.set('');
    this.emailError.set('');
    this.step.set('enter-email');
  }

  onBackToAsk(): void    { this.step.set('ask'); }
  onBackToMethod(): void { this.step.set('choose-method'); }

  // ── Guest management ────────────────────────────────────────────────────────

  onContactPick(contact: ContactItem): void {
    const email = contact.email.trim().toLowerCase();
    if (!email || this.addedGuests().some(g => g.email.toLowerCase() === email)) return;
    this.addedGuests.update(list => [
      ...list,
      { name: `${contact.first_name} ${contact.last_name}`.trim(), email: contact.email },
    ]);
    this.step.set(this._nextStepAfterAdd());
  }

  onEmailConfirm(): void {
    const email = this.emailInput().trim().toLowerCase();
    if (!this._isValidEmail(email)) {
      this.emailError.set('Please enter a valid email address.');
      return;
    }
    if (this.addedGuests().some(g => g.email.toLowerCase() === email)) {
      this.emailError.set('This email has already been added.');
      return;
    }
    this.addedGuests.update(list => [...list, { name: '', email }]);
    this.emailInput.set('');
    this.emailError.set('');
    this.step.set(this._nextStepAfterAdd());
  }

  private _nextStepAfterAdd(): GuestWizardStep {
    return this.mode === 'add-participants' ? 'choose-method' : 'ask';
  }

  onRemoveGuest(email: string): void {
    this.addedGuests.update(list => list.filter(g => g.email !== email));
  }

  onSearchInput(event: Event): void {
    this.contactSearch.set((event.target as HTMLInputElement).value);
  }

  onEmailInput(event: Event): void {
    this.emailInput.set((event.target as HTMLInputElement).value);
    this.emailError.set('');
  }

  onEmailKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') { event.preventDefault(); this.onEmailConfirm(); }
  }

  private _isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}
